/**
 * Cmd-K helpers — pure utilities used by `apps/web/src/components/command-palette.tsx`.
 *
 * Extracted to keep the heavy palette component testable. All exports are
 * side-effect-free and run in vitest without the React runtime.
 *
 * Phase 3 — Cmd-K rebuild. Spec §10 Phase 3, decisions.md §Q8.
 */

import { NAV_V2, type NavV2GroupId } from './nav-v2';

/** Valid v2 scope IDs — mirrors NAV_V2 group IDs (8 domains after Workforce tile). */
const VALID_SCOPE_IDS = new Set<NavV2GroupId>([
  'home',
  'care',
  'ops',
  'workforce',
  'quality',
  'governance',
  'finance',
  'insights',
]);

export interface ScopedQuery {
  /** Resolved v2 scope ID, or null when no scope prefix is present. */
  scope: NavV2GroupId | null;
  /** Search term with the scope token removed. */
  term: string;
}

/**
 * Parse a Cmd-K query into an optional scope + free-text term.
 *
 * Syntax (matches `nav-redesign/shell.jsx:429-440`):
 *   - No prefix:        `'clients'`         → { scope: null, term: 'clients' }
 *   - Scope prefix:     `'> care'`          → { scope: 'care', term: '' }
 *   - Scope + term:     `'> care: clients'` → { scope: 'care', term: 'clients' }
 *   - Scope + term:     `'> care clients'`  → { scope: 'care', term: 'clients' }
 *   - Title-prefix:     `'> qual'`          → { scope: 'quality', term: '' }
 *   - Unknown scope:    `'> garbage'`       → { scope: null, term: '> garbage' }
 *     (treats the whole string as a free-text term)
 *
 * Scope matching is case-insensitive and uses (a) exact group ID OR
 * (b) "title startsWith first-word" (so `> qual` → quality). Empty input
 * after `>` (e.g., `'> '`) returns { scope: null, term: '' }.
 */
export function parseScopedQuery(input: string): ScopedQuery {
  const trimmed = input.trim();
  if (!trimmed.startsWith('>')) return { scope: null, term: trimmed };

  const rest = trimmed.slice(1).trim();
  if (!rest) return { scope: null, term: '' };

  const firstWord = rest.split(/[\s:]/)[0]?.toLowerCase() ?? '';
  if (!firstWord) return { scope: null, term: '' };

  // Match by ID or by title-startsWith-firstWord (case-insensitive).
  const matched = NAV_V2.find(
    (g) => g.id === firstWord || g.title.toLowerCase().startsWith(firstWord),
  );

  if (!matched || !VALID_SCOPE_IDS.has(matched.id)) {
    // Unknown scope token — surface the input as a plain term so the user
    // sees their literal query in the palette (instead of silently dropping
    // the `>` prefix). They can correct the typo.
    return { scope: null, term: trimmed };
  }

  const remaining = rest.slice(firstWord.length).replace(/^[\s:]+/, '');
  return { scope: matched.id, term: remaining };
}

/**
 * Resolve a scope ID to the human-readable title (for chip display).
 * Returns `null` for an unknown scope so callers can render the no-chip
 * state.
 */
export function scopeTitle(scope: NavV2GroupId | null): string | null {
  if (!scope) return null;
  const group = NAV_V2.find((g) => g.id === scope);
  return group?.title ?? null;
}

/**
 * Append `href` to the front of the recents list, deduped and capped.
 *
 * - Most-recent-first ordering.
 * - Duplicates are removed (so re-visiting a recent moves it back to the
 *   top, not appended a second time).
 * - Cap defaults to 6 (matches the prototype). Older entries fall off.
 *
 * Pure — caller persists the returned value via `useUserPreferences`.
 */
export function appendRecent(prev: readonly string[], href: string, cap = 6): string[] {
  if (!href) return [...prev];
  const filtered = prev.filter((h) => h !== href);
  filtered.unshift(href);
  return filtered.slice(0, cap);
}

/**
 * Drop recents whose hrefs are no longer in the visible set (e.g., the
 * user lost RBAC or module access to a previously-recent route). Pure —
 * caller persists if the trimmed list differs.
 */
export function pruneRecents(
  recents: readonly string[],
  visibleHrefs: ReadonlySet<string>,
): string[] {
  return recents.filter((h) => visibleHrefs.has(h));
}
